import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { displayValue } from '../lib/displayValue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Plus, 
  FileText, 
  Camera, 
  MapPin, 
  Trash2, 
  Edit, 
  Eye,
  Save,
  X,
  Download,
  Upload,
  PenTool,
  Loader2,
  ExternalLink,
  Clock,
  CheckCircle,
  RotateCcw,
  RefreshCw,
  Mail,
  Archive,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, getISOWeek, getISOWeekYear } from 'date-fns';
import { parseLocalDate } from '../lib/utils';
import StatTile from '../components/StatTile';
import { ViewModeToggle, useViewMode } from '../components/ViewModeToggle';
import { useAuth } from '../context/AuthContext';
import { photoReadinessTone } from '../utils/fieldReportWorkflow';
import PdfStorageNotice from '../components/field-report/PdfStorageNotice';
import FieldReportCard from '../components/field-report/FieldReportCard';
import FieldReportListRow from '../components/field-report/FieldReportListRow';
import ApprovedTabBody from '../components/field-report/ApprovedTabBody';
import { KIND_COLOR, KIND_LABEL } from '../components/field-report/status';
import { sortReports, DEFAULT_SEARCH_FIELDS } from '../components/field-report/sortReports';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Utility types and color codes
const UTILITY_TYPES = ['Gas', 'Electric', 'Water', 'Telecom', 'Fiber', 'Sewer', 'Storm Drain', 'Cable TV', 'Unknown'];
const UTILITY_COLORS = [
  { value: 'Red', label: 'Red (Electric)', color: '#ef4444' },
  { value: 'Yellow', label: 'Yellow (Gas/Oil)', color: '#eab308' },
  { value: 'Orange', label: 'Orange (Telecom)', color: '#f97316' },
  { value: 'Blue', label: 'Blue (Water)', color: '#3b82f6' },
  { value: 'Green', label: 'Green (Sewer)', color: '#22c55e' },
  { value: 'Purple', label: 'Purple (Reclaimed Water)', color: '#a855f7' },
  { value: 'Pink', label: 'Pink (Temp Survey)', color: '#ec4899' },
  { value: 'White', label: 'White (Proposed)', color: '#f8fafc' }
];
const UTILITY_MATERIALS = ['Steel', 'PVC', 'HDPE', 'Ductile Iron', 'Copper', 'Conduit', 'Cast Iron', 'Concrete', 'Clay', 'Unknown'];
const EXPOSURE_OPTIONS = ['Full', 'Half', 'Side', 'Top', 'Partial'];
const CONDITION_OPTIONS = ['Good', 'Fair', 'Damaged', 'Corroded', 'Unknown'];
const SURFACE_TYPES = ['Asphalt', 'Concrete', 'Soil', 'Gravel', 'Grass', 'Pavers'];
const SOIL_CONDITIONS = ['Sand', 'Clay', 'Loose', 'Compacted', 'Rocky', 'Wet', 'Mixed'];


// Tiny status dot showing whether all photos on a pending field report
// have finished uploading (driven by backend's photos_ready / expected /
// received fields).
//   - green  -> photos_ready === true
//   - amber  -> photos still arriving (pending)
//   - none   -> no tracking data (older records, or no photos at all)
function PhotoReadinessDot({ record }) {
  if (!record || record.status !== 'submitted_pending_review') return null;
  const tone = photoReadinessTone(record);
  if (!tone) return null;
  const exp = Number(record.expected_photos_count) || 0;
  const rec = Number(record.received_photos_count) || 0;
  if (tone === 'ready') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-green-700"
        title="All photos uploaded — ready to review"
        data-testid="photo-ready-dot"
      >
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        Ready
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-700"
      title={`${rec} of ${exp} photos uploaded — wait before approving`}
      data-testid="photo-pending-dot"
    >
      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse inline-block" />
      {rec}/{exp} photos
    </span>
  );
}

export default function FieldReports() {
  const navigate = useNavigate();
  const { token, user, isAdmin, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('precon');
  const [softDigs, setSoftDigs] = useState([]);
  const [preCons, setPreCons] = useState([]);
  const [locates, setLocates] = useState([]);
  // Feb-2026 Phase 4: Confined Space Evaluation forms live on Field
  // Reports now. Backed by /api/maintenance filtered by form_type.
  // Restrictions come from /api/cse-restrictions — used to hide the
  // New/Approve/Reject buttons when the user isn't authorized.
  const [cseForms, setCseForms] = useState([]);
  const [cseRestrictions, setCseRestrictions] = useState({ submitter_ids: [], approver_ids: [] });
  const canSubmitCSE = isAdmin() || (cseRestrictions.submitter_ids || []).includes(user?.id);
  const canApproveCSE = isAdmin() || (cseRestrictions.approver_ids || []).includes(user?.id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // all, draft, submitted_pending_review, completed
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc, status
  const [viewMode, setViewMode] = useViewMode('fieldReports.viewMode');

  // Weekly Field Reports Archive email — admin-only manual trigger for
  // POST /field-reports/archive/send (the same build+email path the
  // Saturday-night scheduled job uses). Defaults to the PREVIOUS full
  // ISO week (matching the scheduled job's own date math) so the common
  // case — resending last week's archive — needs zero typing.
  const [showArchiveEmailDialog, setShowArchiveEmailDialog] = useState(false);
  const [archiveEmailRecipient, setArchiveEmailRecipient] = useState('');
  const [archiveEmailWeek, setArchiveEmailWeek] = useState('');
  const [archiveEmailYear, setArchiveEmailYear] = useState('');
  const [archiveEmailSending, setArchiveEmailSending] = useState(false);
  const [archiveHistory, setArchiveHistory] = useState([]);
  const [archiveHistoryLoading, setArchiveHistoryLoading] = useState(false);
  const [resendingIndex, setResendingIndex] = useState(null);

  const refreshArchiveHistory = () => {
    setArchiveHistoryLoading(true);
    return axios.get(`${API}/field-reports/archive/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setArchiveHistory(res.data.history || []))
      .catch(() => setArchiveHistory([]))
      .finally(() => setArchiveHistoryLoading(false));
  };

  // Load once on mount (admin-only) so the "Email Archive" button can
  // show a red-dot badge for an unresolved failed/missed week without
  // requiring the admin to open the dialog first.
  useEffect(() => {
    if (isAdmin()) {
      refreshArchiveHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasUnresolvedArchiveIssue = archiveHistory.some((h) => h.status === 'failed' || h.status === 'missed');

  const openArchiveEmailDialog = () => {
    const lastWeekAnchor = new Date();
    lastWeekAnchor.setDate(lastWeekAnchor.getDate() - 7);
    setArchiveEmailRecipient(user?.email || '');
    setArchiveEmailWeek(String(getISOWeek(lastWeekAnchor)));
    setArchiveEmailYear(String(getISOWeekYear(lastWeekAnchor)));
    setShowArchiveEmailDialog(true);
    refreshArchiveHistory();
  };

  // Sends now run in a backend BackgroundTask (see sendArchiveEmail's
  // comment), so poll Archive History every 10s while this dialog is
  // open — that's how a queued send's real outcome shows up.
  useEffect(() => {
    if (!showArchiveEmailDialog) return;
    const interval = setInterval(refreshArchiveHistory, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchiveEmailDialog]);

  const sendArchiveEmail = async (recipientEmail, week, year) => {
    const res = await axios.post(
      `${API}/field-reports/archive/send`,
      { recipient_email: recipientEmail, week, year },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const d = res.data;
    // Building/emailing now happens in a background task (see backend
    // routes/field_reports_archive.py) — this response just confirms the
    // job was queued. Archive History (polled every 10s while this
    // dialog is open) and an in-app notification reflect the real
    // outcome once it finishes.
    toast.info(d.message || `Building Week ${d.week}, ${d.year} in the background for ${d.recipient}.`, { duration: 8000 });
    return d;
  };

  const handleSendArchiveEmail = async () => {
    const week = parseInt(archiveEmailWeek, 10);
    const year = parseInt(archiveEmailYear, 10);
    if (!archiveEmailRecipient || !week || !year) {
      toast.error('Recipient email, week, and year are all required.');
      return;
    }
    setArchiveEmailSending(true);
    try {
      await sendArchiveEmail(archiveEmailRecipient, week, year);
      // Dialog stays open (unlike before) so the admin can watch Archive
      // History update once the background send finishes.
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send the archive email');
    } finally {
      setArchiveEmailSending(false);
    }
  };

  // One-click retry straight from an Archive History row — reuses the
  // row's own recipient/week/year so a failed send doesn't require
  // re-typing anything.
  const handleResendArchive = async (row, index) => {
    if (!row.week || !row.year || !row.recipient_email) {
      toast.error('This history entry is missing week/year info — resend it manually instead.');
      return;
    }
    setResendingIndex(index);
    try {
      await sendArchiveEmail(row.recipient_email, row.week, row.year);
      await refreshArchiveHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingIndex(null);
    }
  };

  // Prefill the form from a "missed week" row — there's no known
  // recipient for a run that never happened, so this fills Week/Year and
  // lets the admin pick who to send it to, rather than guessing blind.
  const handleUseMissedWeek = (row) => {
    setArchiveEmailWeek(String(row.week));
    setArchiveEmailYear(String(row.year));
    toast.info(`Week ${row.week}, ${row.year} loaded below — pick a recipient and click Send Archive.`);
  };

  const loadForms = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [jobsRes, teamRes, softDigsRes, preConsRes, locatesRes, cseRes, cseRestrRes] = await Promise.all([
        axios.get(`${API}/jobs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/team`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/soft-digs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/precons`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/locates`, { headers }).catch(() => ({ data: [] })),
        // Feb-2026 Phase 4: Confined Space Evaluations — pulled from the
        // maintenance_forms collection.
        // Perf fix (2026-10-03): now asks the server to filter to CSE
        // forms only via the existing `form_type` param (same one
        // Maintenance.js's own Grease/Maintenance Request tabs already
        // use) instead of pulling every maintenance form ever created
        // and filtering client-side — that unfiltered fetch only grows
        // over time and was slowing this page down. No shape/behavior
        // change: still a plain array, still filtered again below as a
        // safety net.
        axios.get(`${API}/maintenance`, { headers, params: { form_type: 'Confined Space Evaluation' } }).catch(() => ({ data: [] })),
        axios.get(`${API}/cse-restrictions`, { headers }).catch(() => ({ data: { submitter_ids: [], approver_ids: [] } })),
      ]);
      setJobs(jobsRes.data || []);
      setTeam(teamRes.data || []);
      setSoftDigs(softDigsRes.data || []);
      setPreCons(preConsRes.data || []);
      setLocates(locatesRes.data || []);
      const allMaint = Array.isArray(cseRes.data) ? cseRes.data : [];
      // Feb 2026 audit — maintenance uses `draft/pending/approved/
      // rejected`; field reports use `draft/submitted_pending_review/
      // completed`. Normalize CSE to field-report vocabulary so the
      // shared card/badge/list components render the same status
      // colors and filter tabs work uniformly. We mirror the value
      // onto `status` (overwriting the maintenance one for UI use)
      // but stash the original on `_maintenance_status` in case a
      // handler needs it.
      const MAINT_TO_FR = {
        draft: 'draft',
        pending: 'submitted_pending_review',
        approved: 'completed',
        rejected: 'draft', // rejected = editable again in this UI
      };
      const cseNorm = allMaint
        .filter(f => f.form_type === 'Confined Space Evaluation')
        .map(f => ({
          ...f,
          _maintenance_status: f.status,
          status: MAINT_TO_FR[(f.status || 'draft').toLowerCase()] || 'draft',
        }));
      setCseForms(cseNorm);
      setCseRestrictions(cseRestrRes.data || { submitter_ids: [], approver_ids: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  // Warm-up nudge for the production frontend pod.
  //
  // Why this exists (2026-07): production deployments hibernate the
  // frontend container after a few hours of idle. The first
  // `page.goto` from the backend's Playwright renderer (triggered by
  // "Email me the PDF") hits an unresponsive pod and gets
  // `net::ERR_ABORTED`. The backend has a cold-start retry that
  // recovers within ~10s, but this warm-up tries to PREVENT the
  // failure in the first place: by the time the user clicks the
  // email button (typically tens of seconds after the page loads),
  // the pod is already warm.
  //
  // Fires once on mount, fails silently. No UI impact, no error
  // logging, no blocking. Belt-and-braces with the server-side retry.
  useEffect(() => {
    fetch(window.location.origin, { method: 'HEAD', cache: 'no-store' })
      .catch(() => {}); // swallow — warm-up is best-effort only
  }, []);

  // Filter reports by status
  const filterByStatus = (reports) => {
    if (statusFilter === 'all') return reports;
    return reports.filter(r => r.status === statusFilter);
  };

  // Build a search-friendly title for any report type.
  // NOTE: never fall back to the raw UUID — that produced "d0353bc4"-style
  // mystery rows in production when a draft was created but never linked to
  // a job (the now-fixed `ensureDraftId` race). Use a clear placeholder so
  // the operator can identify orphans at a glance.
  //
  // 2026-09-01: Join against the loaded `jobs` list for the identifying
  // "job code - title - address" line. This is the reliable source for
  // ALL 4 report types — per-record fields drift (Soft Dig never
  // captures a title; Confined Space captures neither a title nor
  // job_number on the record itself), but every report carries a job_id.
  // Falls back to per-record fields only for orphaned reports with no
  // resolvable job (deleted job, or the pre-fix race noted above).
  const jobsById = useMemo(() => {
    const map = {};
    (jobs || []).forEach(j => { map[j.id] = j; });
    return map;
  }, [jobs]);

  const getReportTitle = (r) => {
    const job = r.job_id ? jobsById[r.job_id] : null;
    if (job) {
      const joined = [job.job_number, job.job_title, job.service_address].filter(Boolean).join(' - ');
      if (joined) return joined;
    }
    return (
      r.project_name || r.site_location || r.location || r.address ||
      r.form_number || r.job_number || r.job_title || ''
    );
  };
  const getReportDisplayTitle = (r) => getReportTitle(r) || '(Untitled draft)';
  const getReportDate = (r) => (
    r.visit_date || r.date || r.locate_date || r.created_at || ''
  );

  // Card view (FieldReportCard) shows title (bold) + location (grey)
  // as two separate lines — unlike getReportTitle() above which joins
  // everything into one string for the List view. Same job-join source
  // of truth (2026-09-01 consistency fix), split across the card's
  // existing two slots: "job code - job title" on the bold line,
  // address on the location line.
  const getReportJobInfo = (r) => {
    const job = r.job_id ? jobsById[r.job_id] : null;
    const title = (job && [job.job_number, job.job_title].filter(Boolean).join(' - ')) ||
      r.project_name || r.form_number || r.job_number || '(Untitled draft)';
    const location = job?.service_address || r.site_location || r.job_service_address || 'No Location';
    return { title, location };
  };

  // Apply search & sort on top of status filter. Uses the shared
  // sortReports() helper (see components/field-report/sortReports.js)
  // so the Approved tab can't fork the sort logic like it did before
  // iter-121.
  const filterAndSort = (reports) => sortReports(
    filterByStatus(reports),
    sortBy,
    searchQuery,
    {
      getTitle: getReportTitle,
      getDate: getReportDate,
      searchFields: DEFAULT_SEARCH_FIELDS,
    },
  );

  // Get status counts for badges
  const getStatusCounts = (reports) => ({
    all: reports.length,
    draft: reports.filter(r => r.status === 'draft').length,
    submitted_pending_review: reports.filter(r => r.status === 'submitted_pending_review').length,
    completed: reports.filter(r => r.status === 'completed').length
  });

  // Delete handlers for the new field report types
  const deleteFieldReport = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) return;
    // CSE is a maintenance_forms row → uses the /maintenance router;
    // everything else uses the field-report routers.
    const endpoints = { precon: 'precons', softdig: 'soft-digs', locate: 'locates', cse: 'maintenance' };
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/${endpoints[type]}/${id}`, { headers });
      toast.success('Report deleted');
      loadForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete report');
    }
  };

  // Navigate to print preview for field reports. CSE now has its own
  // React preview route that embeds the server-rendered ReportLab PDF
  // in an iframe — same UX as the other three types (Feb 2026 audit).
  const openFieldReportPreview = (type, id) => {
    const previewUrls = {
      precon: `/precon/${id}/preview`,
      softdig: `/soft-dig/${id}/preview`,
      locate: `/locate/${id}/preview`,
      cse: `/confined-space/${id}/preview`,
    };
    navigate(previewUrls[type]);
  };

  // PDF Export functions - Downloads PDF from backend API
  const downloadPDFFromBackend = async (endpoint, filename) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}${endpoint}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Re-archive an approved report's PDF (re-generates with latest photos
  // AND replaces the archived URL — uploads to OneDrive when connected,
  // Cloudinary as fallback).
  const reArchiveAndDownload = async (type, form) => {
    const typeSegment = type === 'precon' ? 'precons'
      : type === 'softdig' ? 'soft-digs'
      : 'locates';
    const label = type === 'precon' ? 'PreCon'
      : type === 'softdig' ? 'SoftDig'
      : 'Locate';
    try {
      toast.info('Re-archiving PDF…');
      const reRes = await axios.post(`${API}/${typeSegment}/${form.id}/re-archive-pdf`);
      const storage = (reRes.data && reRes.data.archived_pdf_storage) || 'storage';
      toast.success(`PDF re-archived to ${storage === 'onedrive' ? 'OneDrive' : storage}`);
      // Refresh the local reports list so the new archived URL/storage shows.
      await loadForms();
      // Also download a fresh copy for the user.
      await downloadPDFFromBackend(
        `/${typeSegment}/${form.id}/pdf`,
        `${label}_${form.form_number || form.id.slice(0, 8)}.pdf`,
      );
    } catch (e) {
      console.error('Re-archive error:', e);
      const detail = e?.response?.data?.detail || 'Failed to re-archive PDF';
      toast.error(detail);
    }
  };

  const exportPreConPDF = (form) => {
    downloadPDFFromBackend(`/precons/${form.id}/pdf`, `PreCon_${form.form_number || form.id.slice(0,8)}.pdf`);
  };

  const exportSoftDigPDF = (form) => {
    downloadPDFFromBackend(`/soft-digs/${form.id}/pdf`, `SoftDig_${form.form_number || form.id.slice(0,8)}.pdf`);
  };

  const exportLocatePDF = (form) => {
    downloadPDFFromBackend(`/locates/${form.id}/pdf`, `Locate_${form.form_number || form.id.slice(0,8)}.pdf`);
  };

  const exportPDF = (type, form) => {
    if (type === 'precon') exportPreConPDF(form);
    else if (type === 'softdig') exportSoftDigPDF(form);
    else exportLocatePDF(form);
  };

  // "Email me the PDF" — server-side generate + SMTP attach. Fire-and-forget
  // from the browser's perspective: backend returns 202 immediately, the PDF
  // is built in a background task, then emailed to the requesting user. This
  // sidesteps the slow-network problem field staff hit when sitting on a
  // 15+ second synchronous download from a corporate-firewalled connection.
  const emailPDFFromBackend = async (type, form) => {
    const typeSegment = type === 'precon' ? 'precons'
      : type === 'softdig' ? 'soft-digs'
      : type === 'cse' ? 'maintenance'
      : 'locates';
    try {
      const res = await axios.post(`${API}/${typeSegment}/${form.id}/email-pdf`);
      const msg = res?.data?.message || `We'll email the PDF to ${res?.data?.recipient || 'you'} shortly.`;
      toast.success(msg, { duration: 8000 });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not queue the PDF email.';
      toast.error(detail);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  // Compact one-line row for list view — used by all three tabs
  const resetApproval = async (kind, id) => {
    const endpointKind = kind === 'softdig' ? 'soft-digs'
      : kind === 'precon' ? 'precons'
      : kind === 'cse' ? 'maintenance'
      : 'locates';
    if (!window.confirm('Reset this approved report back to Pending Review? The manager will need to sign it again.')) return;
    try {
      await axios.post(`${API}/${endpointKind}/${id}/reset-approval`);
      toast.success('Reset to Pending Review — manager can sign now');
      loadForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    }
  };

  // Helper — pre-fills common props (title, email, reset, admin, PhotoReadinessDot)
  // so each caller only supplies kind + item + navigation callbacks.
  // NOT a React component (lowercase name) — that keeps
  // react/no-unstable-nested-components happy without needing to hoist
  // the closure over emailPDFFromBackend/resetApproval out of the
  // page.
  const renderListRow = ({ titleOverride, ...props }) => (
    <FieldReportListRow
      {...props}
      title={titleOverride ?? getReportDisplayTitle(props.item)}
      getReportDate={getReportDate}
      onEmail={() => emailPDFFromBackend(props.kind, props.item)}
      onReset={() => resetApproval(props.kind, props.item.id)}
      isAdmin={isAdmin()}
      PhotoReadinessDot={PhotoReadinessDot}
    />
  );

  return (
    <div className="space-y-6">
      {/* Feb 2026 audit — mobile header layout: at <sm the h1 + button
          bar used flex-row + justify-between so "Field Reports" wrapped
          onto 3 lines while a giant "New … Form" button hogged the
          right. Stacked column layout on mobile fixes it; desktop
          stays identical. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">Field Reports</h1>
          <p className="text-slate-500">PreCon, Soft Dig, Locate, and Confined Space forms</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Approved Reports button */}
          <Button
            onClick={() => navigate('/approved-reports')}
            variant="outline"
            size="sm"
            className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="approved-reports-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Approved Reports
          </Button>
          {/* Pending Review button for users with approve permission */}
          {(isAdmin() || hasPermission('can_approve_field_reports')) && (
            <Button
              onClick={() => navigate('/review-queue')}
              size="sm"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="review-queue-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              Pending Review
            </Button>
          )}
          {/* Admin-only: manually send the Weekly Field Reports Archive
              email (same job the Saturday-night scheduler runs). */}
          {isAdmin() && (
            <Button
              onClick={openArchiveEmailDialog}
              variant="outline"
              size="sm"
              className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50 relative"
              data-testid="send-archive-email-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Email Archive
              {hasUnresolvedArchiveIssue && (
                <span
                  className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"
                  data-testid="archive-issue-badge"
                  title="A recent send failed or a scheduled week was missed"
                />
              )}
            </Button>
          )}
          {activeTab === 'precon' ? (
            <Button onClick={() => navigate('/precon')} size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              <Plus className="w-4 h-4 mr-2" />
              New PreCon Form
            </Button>
          ) : activeTab === 'softdig' ? (
            <Button onClick={() => navigate('/soft-dig')} size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              <Plus className="w-4 h-4 mr-2" />
              New Soft Dig Form
            </Button>
          ) : activeTab === 'locate' ? (
            <Button onClick={() => navigate('/locate')} size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
              <Plus className="w-4 h-4 mr-2" />
              New Locate Form
            </Button>
          ) : activeTab === 'cse' && canSubmitCSE ? (
            <Button
              onClick={() => navigate('/confined-space')}
              size="sm"
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              data-testid="new-cse-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Confined Space Form
            </Button>
          ) : null}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setStatusFilter('all'); }}>
        {/* Mobile-safe tab row (Feb 2026): on mobile the 5 tabs used a
            grid-cols-5 layout at max-w-3xl which forced 78-px cells and
            overlapped labels like "Confined Space (2)" onto neighbors.
            Switched to a horizontal-scroll flex row on <sm and the
            original 5-column grid on >=sm so desktop is unchanged. */}
        <TabsList className="flex sm:grid sm:grid-cols-5 justify-start sm:justify-center gap-1 w-full max-w-3xl h-auto overflow-x-auto sm:overflow-x-visible">
          <TabsTrigger value="precon" className="whitespace-nowrap flex-shrink-0" data-testid="precon-tab-trigger">PreCon ({preCons.length})</TabsTrigger>
          <TabsTrigger value="softdig" className="whitespace-nowrap flex-shrink-0" data-testid="softdig-tab-trigger">Soft Dig ({softDigs.length})</TabsTrigger>
          <TabsTrigger value="locate" className="whitespace-nowrap flex-shrink-0" data-testid="locate-tab-trigger">Locate ({locates.length})</TabsTrigger>
          <TabsTrigger value="cse" className="whitespace-nowrap flex-shrink-0" data-testid="cse-tab-trigger">Confined Space ({cseForms.length})</TabsTrigger>
          <TabsTrigger value="completed" className="whitespace-nowrap flex-shrink-0 text-green-700" data-testid="approved-tab-trigger">
            Approved ({[...preCons, ...softDigs, ...locates, ...cseForms].filter(r => r.status === 'completed').length})
          </TabsTrigger>
        </TabsList>

        {/* Search / Sort / View toggle — shows on all tabs including Approved */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search title, job #, address, team..."
                className="pl-9"
                data-testid="field-reports-search"
              />
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="field-reports-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="field-reports-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Newest first</SelectItem>
                <SelectItem value="date_asc">Oldest first</SelectItem>
                <SelectItem value="name_asc">Name A → Z</SelectItem>
                <SelectItem value="name_desc">Name Z → A</SelectItem>
                <SelectItem value="status">By status</SelectItem>
              </SelectContent>
            </Select>
            <div className="inline-flex">
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>
          </div>


        {/* Status Filter - shows for PreCon, Soft Dig, Locate tabs */}
        {activeTab !== 'completed' && (
          <div className="flex gap-2 mt-3 mb-2 flex-wrap">
            <span className="text-sm text-slate-500 self-center mr-2">Filter:</span>
            {[
              { value: 'all', label: 'All', color: 'bg-slate-100 text-slate-700' },
              { value: 'draft', label: 'Draft', color: 'bg-yellow-100 text-yellow-700' },
              { value: 'submitted_pending_review', label: 'Pending', color: 'bg-blue-100 text-blue-700' },
              { value: 'completed', label: 'Approved', color: 'bg-green-100 text-green-700' }
            ].map(filter => {
              const reports = activeTab === 'precon' ? preCons
                : activeTab === 'softdig' ? softDigs
                : activeTab === 'cse' ? cseForms
                : locates;
              const count = filter.value === 'all' ? reports.length : reports.filter(r => r.status === filter.value).length;
              return (
                <Button
                  key={filter.value}
                  variant={statusFilter === filter.value ? 'default' : 'outline'}
                  size="sm"
                  className={statusFilter === filter.value ? filter.color : ''}
                  onClick={() => setStatusFilter(filter.value)}
                >
                  {filter.label} ({count})
                </Button>
              );
            })}
          </div>
        )}

        <TabsContent value="precon" className="mt-4">
          {/* New PreCon Reports */}
          {filterAndSort(preCons).length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {statusFilter === 'all' ? 'No PreCon Reports' : `No ${statusFilter === 'draft' ? 'Draft' : statusFilter === 'submitted_pending_review' ? 'Pending' : 'Approved'} PreCon Reports`}
              </h3>
              {statusFilter === 'all' && (
                <>
                  <p className="text-slate-500 mb-4">Create your first PreCon Form</p>
                  <Button onClick={() => navigate('/precon')} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-2" />
                    New PreCon Form
                  </Button>
                </>
              )}
            </Card>
          ) : viewMode === 'list' ? (
            <Card className="overflow-hidden">
              {filterAndSort(preCons).map(pc => (
                <React.Fragment key={pc.id}>
                  {renderListRow({
                    kind: 'precon',
                    kindLabel: KIND_LABEL.precon,
                    kindColor: KIND_COLOR.precon,
                    item: pc,
                    onView: () => navigate(`/precon/${pc.id}${pc.status === 'completed' ? '?view=true' : ''}`),
                    onEdit: () => navigate(`/precon/${pc.id}`),
                    onPdf: () => openFieldReportPreview('precon', pc.id),
                    onDelete: () => deleteFieldReport('precon', pc.id),
                  })}
                </React.Fragment>
              ))}
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filterAndSort(preCons).map(pc => (
                <FieldReportCard
                  key={pc.id}
                  kind="precon"
                  kindLabel={KIND_LABEL.precon}
                  kindColor={KIND_COLOR.precon}
                  item={pc}
                  title={getReportJobInfo(pc).title}
                  location={getReportJobInfo(pc).location}
                  meta={[
                    { label: 'Team', value: pc.assigned_team || '-' },
                    { label: 'Date', value: pc.visit_date ? format(parseLocalDate(pc.visit_date) || new Date(), 'MMM d, yyyy') : '-' },
                    { label: 'Photos', value: pc.scouting_photos?.length || 0 },
                  ]}
                  onView={() => navigate(`/precon/${pc.id}${pc.status === 'completed' ? '?view=true' : ''}`)}
                  onEdit={() => navigate(`/precon/${pc.id}`)}
                  onPdf={() => openFieldReportPreview('precon', pc.id)}
                  onEmail={() => emailPDFFromBackend('precon', pc)}
                  onReset={() => resetApproval('precon', pc.id)}
                  onDelete={() => deleteFieldReport('precon', pc.id)}
                  isAdmin={isAdmin()}
                  PhotoReadinessDot={PhotoReadinessDot}
                />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="softdig" className="mt-4">
          {/* Soft Dig Data Sheets */}
          {filterAndSort(softDigs).length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {statusFilter === 'all' ? 'No Soft Dig Reports' : `No ${statusFilter === 'draft' ? 'Draft' : statusFilter === 'submitted_pending_review' ? 'Pending' : 'Approved'} Soft Dig Reports`}
              </h3>
              {statusFilter === 'all' && (
                <>
                  <p className="text-slate-500 mb-4">Create your first Soft Dig Form</p>
                  <Button onClick={() => navigate('/soft-dig')} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-2" />
                    New Soft Dig Form
                  </Button>
                </>
              )}
            </Card>
          ) : viewMode === 'list' ? (
            <Card className="overflow-hidden">
              {filterAndSort(softDigs).map(sd => (
                <React.Fragment key={sd.id}>
                  {renderListRow({
                    kind: 'softdig',
                    kindLabel: KIND_LABEL.softdig,
                    kindColor: KIND_COLOR.softdig,
                    item: sd,
                    onView: () => navigate(`/soft-dig/${sd.id}${sd.status === 'completed' ? '?view=true' : ''}`),
                    onEdit: () => navigate(`/soft-dig/${sd.id}`),
                    onPdf: () => openFieldReportPreview('softdig', sd.id),
                    onDelete: () => deleteFieldReport('softdig', sd.id),
                  })}
                </React.Fragment>
              ))}
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filterAndSort(softDigs).map(sd => (
                <FieldReportCard
                  key={sd.id}
                  kind="softdig"
                  kindLabel={KIND_LABEL.softdig}
                  kindColor={KIND_COLOR.softdig}
                  item={sd}
                  title={getReportJobInfo(sd).title}
                  location={getReportJobInfo(sd).location}
                  meta={[
                    { label: 'Team', value: sd.assigned_team || '-' },
                    { label: 'Date', value: sd.date ? format(parseLocalDate(sd.date), 'MMM d, yyyy') : '-' },
                    { label: 'Utility', value: displayValue(sd.utility_type, '-') },
                  ]}
                  onView={() => navigate(`/soft-dig/${sd.id}${sd.status === 'completed' ? '?view=true' : ''}`)}
                  onEdit={() => navigate(`/soft-dig/${sd.id}`)}
                  onPdf={() => openFieldReportPreview('softdig', sd.id)}
                  onEmail={() => emailPDFFromBackend('softdig', sd)}
                  onReset={() => resetApproval('softdig', sd.id)}
                  onDelete={() => deleteFieldReport('softdig', sd.id)}
                  isAdmin={isAdmin()}
                  PhotoReadinessDot={PhotoReadinessDot}
                />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="locate" className="mt-4">
          {/* Locate Reports */}
          {filterAndSort(locates).length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {statusFilter === 'all' ? 'No Locate Reports' : `No ${statusFilter === 'draft' ? 'Draft' : statusFilter === 'submitted_pending_review' ? 'Pending' : 'Approved'} Locate Reports`}
              </h3>
              {statusFilter === 'all' && (
                <>
                  <p className="text-slate-500 mb-4">Create your first Locate Form</p>
                  <Button onClick={() => navigate('/locate')} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-2" />
                    New Locate Form
                  </Button>
                </>
              )}
            </Card>
          ) : viewMode === 'list' ? (
            <Card className="overflow-hidden">
              {filterAndSort(locates).map(loc => (
                <React.Fragment key={loc.id}>
                  {renderListRow({
                    kind: 'locate',
                    kindLabel: KIND_LABEL.locate,
                    kindColor: KIND_COLOR.locate,
                    item: loc,
                    onView: () => navigate(`/locate/${loc.id}${loc.status === 'completed' ? '?view=true' : ''}`),
                    onEdit: () => navigate(`/locate/${loc.id}`),
                    onPdf: () => openFieldReportPreview('locate', loc.id),
                    onDelete: () => deleteFieldReport('locate', loc.id),
                  })}
                </React.Fragment>
              ))}
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filterAndSort(locates).map(loc => (
                <FieldReportCard
                  key={loc.id}
                  kind="locate"
                  kindLabel={KIND_LABEL.locate}
                  kindColor={KIND_COLOR.locate}
                  item={loc}
                  title={getReportJobInfo(loc).title}
                  location={getReportJobInfo(loc).location}
                  meta={[
                    { label: 'Team', value: loc.assigned_team || '-' },
                    { label: 'Date', value: loc.locate_date ? format(parseLocalDate(loc.locate_date) || new Date(), 'MMM d, yyyy') : '-' },
                    { label: 'Type', value: displayValue(loc.locate_type, '-') },
                    { label: 'Utilities', value: loc.utilities?.length || 0 },
                  ]}
                  onView={() => navigate(`/locate/${loc.id}${loc.status === 'completed' ? '?view=true' : ''}`)}
                  onEdit={() => navigate(`/locate/${loc.id}`)}
                  onPdf={() => openFieldReportPreview('locate', loc.id)}
                  onEmail={() => emailPDFFromBackend('locate', loc)}
                  onReset={() => resetApproval('locate', loc.id)}
                  onDelete={() => deleteFieldReport('locate', loc.id)}
                  isAdmin={isAdmin()}
                  PhotoReadinessDot={PhotoReadinessDot}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Confined Space Evaluation Tab — Feb-2026 Phase 4 + audit
            round: card layout mirrors PreCon / Soft Dig / Locate so
            the button set (Edit / View / PDF / Email PDF / Reset
            Approval / Delete), the red kind-badge, colored status
            pill, and PhotoReadinessDot all match. Approve/reject
            still happen on the Maintenance page. */}
        <TabsContent value="cse" className="mt-4">
          <PdfStorageNotice className="mb-3" />
          {filterAndSort(cseForms).length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {statusFilter === 'all' ? 'No Confined Space Forms' : `No ${statusFilter === 'draft' ? 'Draft' : statusFilter === 'submitted_pending_review' ? 'Pending' : 'Approved'} Confined Space Forms`}
              </h3>
              {statusFilter === 'all' && (canSubmitCSE ? (
                <>
                  <p className="text-slate-500 mb-4">Create your first Confined Space Form</p>
                  <Button
                    onClick={() => navigate('/confined-space')}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="cse-empty-new-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Confined Space Form
                  </Button>
                </>
              ) : (
                <p className="italic text-slate-500">
                  Only authorized submitters can create a Confined Space Form.
                </p>
              ))}
            </Card>
          ) : viewMode === 'list' ? (
            <Card className="overflow-hidden">
              {filterAndSort(cseForms).map(cse => (
                <React.Fragment key={cse.id}>
                  {renderListRow({
                    kind: 'cse',
                    kindLabel: KIND_LABEL.cse,
                    kindColor: KIND_COLOR.cse,
                    item: cse,
                    onView: () => navigate(`/confined-space/${cse.id}`),
                    onEdit: () => navigate(`/confined-space/${cse.id}`),
                    onPdf: () => openFieldReportPreview('cse', cse.id),
                    onDelete: () => deleteFieldReport('cse', cse.id),
                  })}
                </React.Fragment>
              ))}
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filterAndSort(cseForms).map(cse => (
                <FieldReportCard
                  key={cse.id}
                  kind="cse"
                  kindLabel={KIND_LABEL.cse}
                  kindColor={KIND_COLOR.cse}
                  item={cse}
                  title={getReportJobInfo(cse).title}
                  location={getReportJobInfo(cse).location}
                  meta={[
                    { label: 'Submitted by', value: cse.submitter_name || cse.created_by || '-' },
                    // Feb 2026 audit — use the same getReportDate()
                    // helper as the list view so card + list can't
                    // show different dates for the same record
                    // (iter119 finding).
                    {
                      label: 'Date',
                      value: getReportDate(cse)
                        ? format(parseLocalDate(getReportDate(cse)) || new Date(getReportDate(cse)), 'MMM d, yyyy')
                        : '-',
                    },
                    { label: 'Job', value: cse.job_number || '-' },
                  ]}
                  onView={() => navigate(`/confined-space/${cse.id}`)}
                  onEdit={() => navigate(`/confined-space/${cse.id}`)}
                  onPdf={() => openFieldReportPreview('cse', cse.id)}
                  onEmail={() => emailPDFFromBackend('cse', cse)}
                  onReset={() => resetApproval('cse', cse.id)}
                  onDelete={() => deleteFieldReport('cse', cse.id)}
                  isAdmin={isAdmin()}
                  PhotoReadinessDot={PhotoReadinessDot}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Completed Reports Tab — implementation lives in
            components/field-report/ApprovedTabBody.jsx now that both
            its subviews only compose the shared FieldReportCard +
            FieldReportListRow. */}
        <TabsContent value="completed" className="mt-4">
          <ApprovedTabBody
            preCons={preCons}
            softDigs={softDigs}
            locates={locates}
            cseForms={cseForms}
            sortBy={sortBy}
            searchQuery={searchQuery}
            viewMode={viewMode}
            getReportDisplayTitle={getReportDisplayTitle}
            getReportDate={getReportDate}
            navigate={navigate}
            openFieldReportPreview={openFieldReportPreview}
            emailPDFFromBackend={emailPDFFromBackend}
            resetApproval={resetApproval}
            isAdmin={isAdmin()}
            PhotoReadinessDot={PhotoReadinessDot}
            renderListRow={renderListRow}
          />
        </TabsContent>
      </Tabs>

      {/* Weekly Field Reports Archive — manual send (admin-only) */}
      <Dialog open={showArchiveEmailDialog} onOpenChange={setShowArchiveEmailDialog}>
        <DialogContent data-testid="archive-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Field Reports Archive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Builds a zip of every approved PreCon, Soft Dig, Locate, and Confined Space report PDF
              for the chosen week and emails it — same as the automatic Saturday-night send.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-email-recipient">Recipient Email</Label>
              <Input
                id="archive-email-recipient"
                type="email"
                value={archiveEmailRecipient}
                onChange={(e) => setArchiveEmailRecipient(e.target.value)}
                placeholder="name@company.com"
                data-testid="archive-email-recipient-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="archive-email-week">Week Number</Label>
                <Input
                  id="archive-email-week"
                  type="number"
                  min="1"
                  max="53"
                  value={archiveEmailWeek}
                  onChange={(e) => setArchiveEmailWeek(e.target.value)}
                  data-testid="archive-email-week-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="archive-email-year">Year</Label>
                <Input
                  id="archive-email-year"
                  type="number"
                  value={archiveEmailYear}
                  onChange={(e) => setArchiveEmailYear(e.target.value)}
                  data-testid="archive-email-year-input"
                />
              </div>
            </div>

            {/* Archive History — last several send attempts (scheduled + manual) */}
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Archive History</p>
              {archiveHistoryLoading ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              ) : archiveHistory.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="archive-history-empty">No archives sent yet.</p>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1.5" data-testid="archive-history-list">
                  {archiveHistory.map((h, i) => h.status === 'missed' ? (
                    <div
                      key={i}
                      className="flex items-center justify-between text-sm bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5"
                      data-testid={`archive-history-row-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <span className="text-amber-700 truncate">
                          Week {h.week}, {h.year} — no attempt logged (expected {h.expected_at})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUseMissedWeek(h)}
                        className="text-xs text-amber-700 hover:text-amber-900 font-medium underline flex-shrink-0 ml-2"
                        data-testid={`archive-history-use-missed-${i}`}
                      >
                        Use this week
                      </button>
                    </div>
                  ) : h.status === 'stuck' ? (
                    <div
                      key={i}
                      className="flex items-center justify-between text-sm bg-orange-50 border border-orange-200 rounded px-2.5 py-1.5"
                      data-testid={`archive-history-row-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                        <span className="text-orange-700 truncate">
                          Week {h.week}, {h.year} — stuck (started {h.pending_minutes}m ago, never finished)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUseMissedWeek(h)}
                        className="text-xs text-orange-700 hover:text-orange-900 font-medium underline flex-shrink-0 ml-2"
                        data-testid={`archive-history-use-stuck-${i}`}
                      >
                        Retry this week
                      </button>
                    </div>
                  ) : (
                    <div
                      key={i}
                      className="flex items-center justify-between text-sm bg-slate-50 rounded px-2.5 py-1.5"
                      data-testid={`archive-history-row-${i}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${h.status === 'sent' ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-slate-600 truncate">
                          {(h.sent_at || h.failed_at || '').slice(0, 10)} — {h.recipient_email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-slate-400">
                          {h.status === 'failed' ? 'failed' : h.delivery_method === 'download_link' ? 'link' : 'attached'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleResendArchive(h, i)}
                          disabled={resendingIndex !== null || !h.week || !h.year}
                          title={`Resend to ${h.recipient_email}`}
                          className="text-slate-400 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid={`archive-history-resend-${i}`}
                        >
                          {resendingIndex === i ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowArchiveEmailDialog(false)}
              disabled={archiveEmailSending}
              data-testid="archive-email-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendArchiveEmail}
              disabled={archiveEmailSending}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              data-testid="archive-email-send-btn"
            >
              {archiveEmailSending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Archive
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
